/**
 * Tooltip definitions for technical terms in the ZFS Calculator
 */

export interface TooltipDefinition {
  term: string;
  explanation: string;
  detailedInfo?: string;
  externalLinks?: { title: string; url: string }[];
}

export const TOOLTIPS: Record<string, TooltipDefinition> = {
  // Drive parameters
  URE: {
    term: "URE (Unrecoverable Read Error)",
    explanation: "A read error that cannot be corrected by drive ECC. RAIDZ2/3 can recover from single UREs during resilver using dual/triple parity.",
    detailedInfo: "Consumer drives: $1$ error per $10^{14}$ bits read. Enterprise drives: $1$ error per $10^{15}$ bits read. RAIDZ2 requires $2+$ UREs in the same stripe for data loss (extremely rare, $\\sim 10^{-20}$ probability).",
  },
  ureDuringResilver: {
    term: "URE During Resilver",
    explanation: "Probability of encountering an unrecoverable read error while rebuilding a failed drive.",
    detailedInfo: "Base Formula: $$P = 1 - (1 - R_{URE})^{Bits_{read}}$$ During resilver, ZFS reads from ALL surviving disks, so $Bits_{read} = Drive_{Size} \\times Surviving_{Disks}$. Critical URE Formula: $$P_{critical} = P_{ure} \\times 0.0001 \\times 0.01^{(parity-1)}$$ for RAIDZ2/3 (single UREs are recoverable with remaining parity). Scrubs reduce this by limiting latent error accumulation window.",
  },
  driveType: {
    term: "Drive Type",
    explanation: "Consumer drives have higher URE rates (1 in 10^14 bits). Enterprise drives have lower URE rates (1 in 10^15 bits) for better reliability.",
    detailedInfo: "Enterprise drives (NAS/NAS Pro, Exos, Ultrastar) are designed for 24/7 operation and have better error recovery. Consumer drives (Desktop, Red) may have higher failure rates in RAID arrays.",
  },
  AFR: {
    term: "AFR (Annualized Failure Rate)",
    explanation: "The percentage of drives expected to fail in one year, based on manufacturer specs.",
    detailedInfo: "Backblaze data shows consumer drives: 1-2%, enterprise drives: 0.5-1.5% typically.",
  },
  driveAge: {
    term: "Drive Age",
    explanation: "Age of drives in years. For homelab users buying used enterprise drives, this adjusts AFR based on Backblaze's real-world failure data.",
    detailedInfo: "Backblaze 2025 data shows failure rates increase with age: New (1.3%), Stable 1-6 yrs (~1%), Elevated 6-8 yrs (1.6-2.5%), High 8-10 yrs (2.5-3.5%), Critical 10+ yrs (4.25% peak). Age 0 = new drive (uses manufacturer AFR).",
    externalLinks: [
      { title: "Backblaze Bathtub Curve Analysis (2025)", url: "https://www.backblaze.com/blog/are-hard-drives-getting-better-lets-revisit-the-bathtub-curve/" },
    ],
  },
  driveIOPS: {
    term: "Drive IOPS",
    explanation: "Input/Output Operations Per Second - measure of random read/write performance for a single drive.",
    detailedInfo: "HDDs: 80-200 IOPS. SSDs: 10,000-100,000 IOPS. Depends on queue depth.",
  },
  peakSequentialSpeed: {
    term: "Peak Sequential Transfer Rate",
    explanation: "Maximum data transfer rate for sequential reads/writes, typically in MB/s.",
    detailedInfo: "HDDs: 150-300 MB/s. SSDs: 500-7000 MB/s depending on interface (SATA/NVMe).",
  },

  // ZFS parameters
  ashift: {
    term: "ashift",
    explanation: "ZFS block size exponent. ashift=12 (4KB) matches modern drives. ashift=9 (512B) only for legacy drives.",
    detailedInfo: "Mismatched ashift causes severe performance degradation. Always use ashift=12 for drives >1TB.",
  },
  slop: {
    term: "slop space",
    explanation: "Reserved space (1/32 of pool) for ZFS metadata and internal operations. Cannot be used for user data.",
    detailedInfo: "ZFS automatically reserves ~3.125% of pool capacity for internal operations.",
  },
  recordsize: {
    term: "recordsize",
    explanation: "The maximum block size for ZFS files. Larger values for sequential workloads, smaller for random access.",
    detailedInfo: "Default 128KB. Use 16KB for databases (VMs), 1MB for media storage.",
  },
  compressionRatio: {
    term: "Compression Ratio",
    explanation: "The ratio of uncompressed data size to compressed data size. 2.0 means data compresses to half its original size.",
    detailedInfo: "LZ4 compression typically achieves 1.5-2.0x on text/log data, 1.0-1.1x on already compressed media.",
  },

  // Reliability metrics
  MTTP: {
    term: "MTTP (Mean Time to Pool Loss)",
    explanation: "Expected time until catastrophic data loss based on drive failure rates and resilver times.",
    detailedInfo: "Formula: $$MTTP = \\frac{1}{P_{pool}}$$ Higher is better. Values in years depend on drive count, RAID level, and failure rates. With Weibull distribution enabled, uses numerical integration for bathtub curve modeling.",
  },
  resilverTime: {
    term: "Resilver Time",
    explanation: "Time to rebuild drive after failure. Longer times increase window for additional failures.",
    detailedInfo: "Formula: $$T = \\frac{D_{data} \\times 1.10}{S_{peak} \\times ZBR \\times Penalty}$$ Accounts for Zoned-Bit Recording (ZBR) slowdown as pool fills, ZFS resilver penalty (mirrors: $0.80$, RAIDZ1: $0.50$, RAIDZ2: $0.45$, RAIDZ3: $0.40$), and $10\\%$ metadata buffer.",
  },
  writePenalty: {
    term: "Write Penalty",
    explanation: "Performance cost of writing to RAIDZ. RAIDZ1=2x, RAIDZ2=3x, RAIDZ3=4x, Mirror=2x.",
    detailedInfo: "Random writes especially affected. Sequential writes less affected.",
  },
  maxTolerableFailures: {
    term: "Max Tolerable Failures",
    explanation: "The number of drive failures that can occur without data loss, based on RAID level.",
    detailedInfo: "Mirror: $1$ (per pair), RAIDZ1: $1$, RAIDZ2: $2$, RAIDZ3: $3$. Exceeding this causes data loss.",
  },
  vdevFailureProbability: {
    term: "VDEV Failure Probability",
    explanation: "Annual probability of a single VDEV (virtual device) failure based on its component drives.",
    detailedInfo: "Formula: $$P_{vdev} = \\sum_{k > parity} \\binom{n}{k} p^k (1-p)^{n-k}$$ Uses binomial distribution to sum all failure scenarios exceeding parity capacity, where $n = drivesPerVdev$ and $p = AFR/100$.",
  },
  poolFailureProbability: {
    term: "Pool Failure Probability",
    explanation: "Annual probability of complete pool data loss. All VDEVs must fail for pool loss.",
    detailedInfo: "Formula: $$P_{pool} = 1 - (1 - P_{vdev})^V$$ More VDEVs = higher linear risk increase, NOT lower risk. Each additional VDEV multiplies the failure surface.",
  },
  multiDriveFailure: {
    term: "Multi-Drive Failure During Resilver",
    explanation: "Probability of additional drive failures during the resilver window, which compounds the risk of data loss.",
    detailedInfo: "Formula: $$P = \\sum_{k \\ge parity_{needed}} P(k; n_{remaining}, p_{resilver})$$ Uses binomial distribution with time-adjusted failure probability: $$p_{resilver} = 1 - (1 - AFR)^{T_{resilver} / 8760}$$ Conservative approximation that assumes simultaneous failures.",
  },
  combinedDataLossProbability: {
    term: "Combined Data Loss Probability",
    explanation: "Total probability of catastrophic data loss from both URE errors and drive failures during resilver.",
    detailedInfo: "Formula: $$P_{total} = 1 - (1 - P_{ure})(1 - P_{multi})$$ RAIDZ2/3 can recover from single UREs using dual/triple parity. URE risk shown is probability of multiple UREs in same stripe ($\\sim 10^{-20}$ to $10^{-30}$). Multi-drive failure is typically the dominant risk factor.",
  },
  riskLevel: {
    term: "Risk Level",
    explanation: "Qualitative assessment of your pool's reliability based on combined data loss probability.",
    detailedInfo: "LOW: $<1\\%$ annual risk. MEDIUM: $1-5\\%$ risk. HIGH: $5-10\\%$ risk. CRITICAL: $>10\\%$ risk. Consider more parity or smaller vdevs for HIGH/CRITICAL levels.",
  },

  // Advanced MTTP / Weibull
  useWeibullDistribution: {
    term: "Weibull Distribution",
    explanation: "Advanced failure model using Weibull distribution instead of constant rate (exponential). Models infant mortality and wear-out.",
    detailedInfo: "Standard model assumes constant failure rate. Weibull captures bathtub curve: early failures, normal operation, wear-out. Use for aged drive pools or critical data.",
  },
  weibullShapeParameter: {
    term: "Weibull Shape Parameter (β)",
    explanation: "Controls failure rate pattern: β<1 = decreasing (infant mortality), β=1 = constant, β>1 = increasing (wear-out).",
    detailedInfo: "New drives: β=0.5-1.0 (early failures). Normal operation: β=1.0 (constant rate). Old drives: β=1.5-3.0 (accelerated wear).",
  },

  // Performance metrics
  IOPS: {
    term: "IOPS",
    explanation: "Input/Output Operations Per Second - measure of random read/write performance.",
    detailedInfo: "HDDs: $80-200$ IOPS. SSDs: $10{,}000-100{,}000$ IOPS. Depends on queue depth.",
  },
  randomWriteIOPS: {
    term: "Random Write IOPS",
    explanation: "Measured random write performance of the pool after accounting for write penalty.",
    detailedInfo: "Formula: $$IOPS_{write} = \\frac{Drives \\times Drive_{IOPS}}{Penalty}$$ RAIDZ1: $Penalty=2$, RAIDZ2: $Penalty=3$, RAIDZ3: $Penalty=4$, Mirrors: $Penalty=2$. Mirrors maintain single-drive IOPS per vdev.",
  },
  randomReadIOPS: {
    term: "Random Read IOPS",
    explanation: "Measured random read performance of the pool across all drives.",
    detailedInfo: "Formula: $$IOPS_{read} = Drives \\times Drive_{IOPS}$$ (scales linearly). Mirrors can read from either side ($2\\times$ boost per pair). RAIDZ spreads reads across all drives.",
  },
  effectiveIOPS: {
    term: "Effective IOPS",
    explanation: "Combined IOPS based on your read/write percentage mix and workload type.",
    detailedInfo: "Formula: $$IOPS_{eff} = (IOPS_{read} \\times Read\\%) + (IOPS_{write} \\times Write\\%)$$ Calculated from read IOPS, write IOPS, and your specified read percentage.",
  },
  sequentialThroughput: {
    term: "Sequential Throughput",
    explanation: "Data transfer rate for large sequential reads/writes in MB/s.",
    detailedInfo: "Formula: $$Throughput = Drives \\times Drive_{Speed} \\times ZBR_{multiplier}$$ Scales with drive count but limited by Zoned-Bit Recording slowdown as pool fills ($25\\% \\to 100\\%$ full: $1.00\\times \\to 0.45\\times$ speed).",
  },
  bottleneck: {
    term: "Performance Bottleneck",
    explanation: "The limiting factor in your storage configuration's performance.",
    detailedInfo: "Could be drive IOPS (random workloads), sequential speed (large files), or write penalty (write-heavy workloads). Identified by comparing theoretical vs actual performance.",
  },

  // Economics
  hardwareCost: {
    term: "Hardware Cost",
    explanation: "Upfront cost to purchase all drives for the pool.",
    detailedInfo: "Formula: $$Cost = Total_{Drives} \\times Drive_{Cost}$$ This is your initial investment before considering power or replacements.",
  },
  totalDrives: {
    term: "Total Drives",
    explanation: "Total number of physical drives in the pool.",
    detailedInfo: "Formula: $$Total = Drives_{PerVDEV} \\times VDEV_{Count}$$ More drives = higher capacity but also higher cost and power consumption.",
  },
  costPerTiB: {
    term: "Cost per TiB",
    explanation: "Total hardware and power cost per tebibyte of usable storage, factoring in RAID overhead.",
    detailedInfo: "Formula: $$Cost/TiB = \\frac{Hardware_{Cost}}{Usable_{TiB} \\times (\\frac{1024}{1000})^4}$$ Converts TiB to TB for standard pricing units. Includes 5-year power cost at specified electricity rate.",
  },
  idlePower: {
    term: "Idle Power",
    explanation: "Total power consumption when drives are spinning but not actively reading/writing.",
    detailedInfo: "Formula: $$Watts_{Idle} = Total_{Drives} \\times Drive_{WattsIdle}$$ HDDs consume less power at idle. This represents baseline power draw $24/7$.",
  },
  loadPower: {
    term: "Load Power",
    explanation: "Total power consumption during active read/write operations.",
    detailedInfo: "Formula: $$Watts_{Load} = Total_{Drives} \\times Drive_{WattsLoad}$$ HDDs consume more power under load. Use this for calculating peak power requirements for UPS sizing.",
  },
  usableTiB: {
    term: "Usable TiB",
    explanation: "Actual storage available for data after accounting for RAID parity and ZFS overhead.",
    detailedInfo: "Varies by RAID level: RAIDZ1 loses $1$ drive to parity, RAIDZ2 loses $2$, RAIDZ3 loses $3$, mirrors use $50\\%$. Also accounts for ZFS slop space ($\\sim 3.125\\%$).",
  },
  totalWatts: {
    term: "Power Consumption",
    explanation: "Total power draw of all drives in the pool, in watts.",
    detailedInfo: "Idle power is lower than load power. Consider $24/7$ operation for annual costs.",
  },
  annualKWh: {
    term: "Annual Power Consumption",
    explanation: "Total energy consumed by the pool in one year, in kilowatt-hours (kWh).",
    detailedInfo: "Formula: $$kWh = \\frac{Watts \\times Hours_{PerDay} \\times 365}{1000}$$ Based on specified uptime hours per day and average of idle/load power.",
  },
  tco: {
    term: "TCO (Total Cost of Ownership)",
    explanation: "5-year cost including hardware, power, and expected drive replacements.",
    detailedInfo: "Formula: $$TCO = Hardware + (Power \\times 5) + \\sum(Expected_{Failures} \\times Drive_{Cost})$$ Helps compare true long-term costs between different configurations.",
  },
  expectedFailures: {
    term: "Expected Failures",
    explanation: "Statistical estimate of how many drives will fail per year based on AFR.",
    detailedInfo: "Formula: $$Failures = Total_{Drives} \\times \\frac{AFR}{100}$$ Based on drive AFR and total drive count. Actual failures may vary.",
  },

  // Capacity
  poolFillPercent: {
    term: "Pool Fill Level",
    explanation: "Percentage of pool capacity currently used. Performance degrades above 80%.",
    detailedInfo: "Affects resilver time via Zoned-Bit Recording (ZBR): higher fill = slower inner track speeds ($100\\%$ full = $45\\%$ of peak speed). Also affects fragmentation and copy-on-write efficiency.",
  },
  scrubIntervalDays: {
    term: "Scrub Interval",
    explanation: "How often ZFS reads all data to detect and repair silent errors. Regular scrubs reduce data loss risk.",
    detailedInfo: "Monthly scrubs ($30$ days) are recommended. More frequent scrubs catch errors before they combine with drive failures. Reduces URE risk window: $$window_{effective} = \\min(T_{resilver}, T_{scrub})$$",
  },
  rawCapacity: {
    term: "Raw Capacity",
    explanation: "Total physical storage of all drives before RAID and ZFS overhead.",
    detailedInfo: "Formula: $$Raw = Total_{Drives} \\times Drive_{Size}$$ Does not account for parity or metadata.",
  },
  parityOverhead: {
    term: "Parity Overhead",
    explanation: "Storage used for redundancy (RAID parity) instead of user data.",
    detailedInfo: "RAIDZ1: $1$ drive lost, RAIDZ2: $2$ drives lost, RAIDZ3: $3$ drives lost, Mirrors: $50\\%$ lost. Formula: $$Parity_{Drives} = \\lceil Drives_{PerVDEV} \\times Parity_{Ratio} \\rceil$$",
  },
  p1Padding: {
    term: "P+1 Padding",
    explanation: "Extra space reserved for padding to align writes properly across RAID stripes.",
    detailedInfo: "ZFS adds padding to optimize write alignment and reduce fragmentation. Typically $1$ sector per stripe.",
  },
  zfsOverhead: {
    term: "ZFS Overhead",
    explanation: "Space used for metadata, slop space, and 256-bit checksums.",
    detailedInfo: "Includes $\\sim 3.125\\%$ slop space ($1/32$ of pool), metadata for block pointers, and checksum overhead. ZFS checksums every block with $256$-bit checksum.",
  },
  finalUsable: {
    term: "Final Usable Capacity",
    explanation: "Total storage available for data after accounting for all ZFS overhead and parity.",
    detailedInfo: "Formula: $$Usable = Raw - Parity - Padding - Overhead - Slop$$ Multiplied by compression ratio if enabled. This is the theoretical maximum.",
  },
  practicalUsable: {
    term: "Practical Usable Capacity",
    explanation: "Actual usable space after reserving free space for optimal ZFS performance.",
    detailedInfo: "Formula: $$Practical = Final_{Usable} \\times (1 - FreeSpace_{Limit})$$ ZFS requires free space for copy-on-write operations and metadata to maintain performance.",
  },
  freeSpaceLimit: {
    term: "Reserved (Free Space Limit)",
    explanation: "Space kept free to maintain performance as the pool fills.",
    detailedInfo: "ZFS performance degrades significantly above $80\\%$ full. Keeping $10-20\\%$ free ensures consistent performance and allows copy-on-write operations.",
  },

  // Storage topology
  raidLevel: {
    term: "RAID Level",
    explanation: "Redundancy scheme: Mirror (fastest), RAIDZ1 (1 parity), RAIDZ2 (2 parity), RAIDZ3 (3 parity).",
    detailedInfo: "Mirrors: Best performance, 50% efficiency. RAIDZ2: Recommended balance - survives 1 drive failure AND recovers from UREs during resilver. RAIDZ3: Maximum protection for critical data.",
  },
  vdev: {
    term: "VDEV (Virtual Device)",
    explanation: "A building block of a ZFS pool. Can be a mirror or RAIDZ group of drives.",
    detailedInfo: "Pool = sum of VDEVs. Performance = slowest VDEV. Balance VDEVs for optimal performance.",
  },
  drivesPerVdev: {
    term: "Drives per VDEV",
    explanation: "Number of drives in each RAIDZ or mirror group.",
    detailedInfo: "RAIDZ1: minimum 3, RAIDZ2: minimum 4, RAIDZ3: minimum 5. Mirror: always 2.",
  },
  vdevCount: {
    term: "VDEV Count",
    explanation: "Number of RAIDZ or mirror groups in the pool.",
    detailedInfo: "More VDEVs = better performance, but higher cost. Keep VDEVs balanced.",
  },

  // Workload
  workloadType: {
    term: "Workload Type",
    explanation: "Typical usage pattern: database (random I/O), media (sequential), mixed, backup (write-heavy).",
    detailedInfo: "Affects optimal recordsize, compression, and RAID configuration choices.",
  },
  readPercentage: {
    term: "Read Percentage",
    explanation: "Percentage of I/O operations that are reads vs writes.",
    detailedInfo: "Affects effective IOPS calculation. Most workloads are read-heavy (60-90%).",
  },

  // Optimization
  safetyVsCost: {
    term: "Safety vs Cost Tradeoff",
    explanation: "Optimizer preference: safety (higher RAID level, more redundancy) vs cost (higher efficiency).",
    detailedInfo: "Higher safety = RAIDZ3/mirrors. Higher cost efficiency = RAIDZ1/RAIDZ2.",
  },
};
